/**
 * Client app state: the module-scope reactive signals and the small enum/constant
 * tables shared across the screens. Kept in a leaf module (imports only api types +
 * kerf's `signal`) so every view/loader can import from here without import cycles.
 */
import { signal } from 'kerfjs';

import type { AssignableMembersResponse, DocumentAnnotation } from '../../shared/contract/demands.js';
import type {
  DocumentRevisionT,
  SourceEvidenceResponse,
  SourceRowSearchResponse,
} from '../../shared/contract/library.js';
import type { MyOrganizationsResponse } from '../../shared/contract/orgs.js';
import type { SettingsTab } from '../lib/context.js';
import { EMPTY_MATRIX_SELECTION, type MatrixPosition, type MatrixSelection } from '../lib/matrix-selection.js';
import { loadBool } from '../lib/prefs.js';
import type { Provenance } from '../lib/provenance.js';
import type { SettingsPage } from '../lib/settings-nav.js';
import type { ShellSearchToken } from '../lib/shell-search.js';
import type { ExplicitSavePage } from '../lib/unsaved-settings.js';
import type {
  AiUsageSummary,
  AnalysisReport,
  AnalysisRowT,
  BackgroundDocT,
  ChatMessageT,
  ChatTarget,
  ConversationSummaryT,
  DecisionSummaryT,
  DecisionT,
  DemandListItem,
  DemandSavingsT,
  FactT,
  Me,
  OrgSavingsT,
  ProjectT,
  QuoteMatrix,
  VendorDetailT,
  VendorsOverviewT,
} from './api.js';
import type { QuoteMatrixLoadState } from './quote-matrix-loader.js';

export type Status = 'loading' | 'anon' | 'onboarding' | 'suspended' | 'choosing-organization' | 'ready';

// AI form (FormRequest) shapes, shared by the loaders (answer/skip) and the
// questions dialog (QuestionsDialog). HS2-ZB7GJN.
export interface FormFieldSpec {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: { value: string; label: string }[];
}
export interface FormSpec {
  title: string;
  intro?: string;
  fields: FormFieldSpec[];
  allowSkip?: boolean;
  targetRole?: string;
}
export interface FormRow {
  id: string;
  status: string;
  reason?: string | null;
  schema: FormSpec;
}
export type QuestionsDraft = Record<string, string | boolean>;

// Intake/extraction row + document shapes (HS2-ZB7GJN).
export interface IntakeLine {
  id: string;
  lineNumber: number;
  description: string;
  normalizedDescription: string | null;
  quantity: string | null;
  unit: string | null;
  needsClarification: boolean;
  provenance: Provenance | null;
  // AI-proposed HS code + duty (FR-3.21); hsCodeConfirmedAt is null until confirmed.
  hsCode?: string | null;
  hsCodeDutyPct?: string | null;
  hsCodeConfirmedAt?: string | null;
}
export interface IntakeDoc {
  id: string;
  filename: string;
  kind?: string;
  sizeBytes?: number | null;
  mimeType?: string | null;
  extractionStatus?: string;
  extractionResult?: {
    proposedKind?: string;
    kindMismatch?: boolean;
    linesProposed?: number;
    quotationsCreated?: number;
    extractionError?: string;
  };
}

/** Live upload count before the worker's per-document extraction status appears. */
export const multifileUploadProgress = signal<{ uploaded: number; total: number } | null>(null);

export const status = signal<Status>('loading');
export const me = signal<Me | null>(null);
export const view = signal<
  'demands' | 'workspace' | 'intake' | 'settings' | 'savings' | 'vendors' | 'library' | 'conversations'
>('demands');
// Navigation-overhaul shell state (HS2-BY1SX8): the sidebar can collapse; Settings
// splits into organization / project / personal surfaces reached from the profile
// menu; and the Demands sidebar's Drafts group is collapsed by default.
// The collapsed state of both shell rails persists per-viewer in localStorage
// (HS2-9G5PQ9); loadBool falls back to expanded when storage is unavailable.
export const sidebarCollapsed = signal<boolean>(loadBool('sidebarCollapsed', false));
// The right AI-assistant panel is always present but collapsible (HS2-G7SRW3).
export const inspectorCollapsed = signal<boolean>(loadBool('inspectorCollapsed', false));
export const settingsTab = signal<SettingsTab>('personal');
/** The open settings sub-page within `settingsTab` (HS2-91S2YF). */
export const settingsPage = signal<SettingsPage>('preferences');
/** The settings sidebar accordion folds the active area away when its header is toggled. */
export const settingsAreaCollapsed = signal<boolean>(false);
export const draftsExpanded = signal<boolean>(false);
export const cancelledExpanded = signal<boolean>(false);
// The Chat sidebar's "Closed Chats" group is collapsed by default (HS2-AEX5AM),
// mirroring the Demands sidebar's Drafts/Cancelled groups.
export const closedChatsExpanded = signal<boolean>(false);
// Collapsible toolbar search that filters the active view's sidebar list.
export const shellSearch = signal<string>('');
export const shellSearchTokens = signal<ShellSearchToken[]>([]);
export const searchExpanded = signal<boolean>(false);
export const settings = signal<Record<string, unknown> | null>(null);
export const aiUsage = signal<AiUsageSummary | null>(null);
export const aiUsageLoadState = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
export const orgs = signal<MyOrganizationsResponse['organizations']>([]);
export const notice = signal<string>('');

export const demands = signal<DemandListItem[]>([]);
/** Paging state of the shown demand list (HS2-X9K02S): the next page's cursor, the
 *  per-status totals of every matching demand, the filter key the shown rows answer,
 *  and whether a "Load more" request is in flight. */
export const demandsNextCursor = signal<string | null>(null);
export const demandStatusCounts = signal<Record<string, number>>({});
export const demandListLoadedKey = signal<string>('');
export const demandsLoadingMore = signal<boolean>(false);
export const demandStatusFilter = signal<string>('');
/** The staff members a demand can be assigned to, for the Info control and row menu (FR-5.20). Staff only. */
export const assignableMembers = signal<AssignableMembersResponse['members']>([]);
export const demandSiteFilter = signal<string>('');
export const demandSearch = signal<string>('');
export const workspace = signal<Record<string, unknown> | null>(null);
export const sourceEvidence = signal<SourceEvidenceResponse | null>(null);
/** The open "Find a source row" picker for one demand line (HS2-4TER5Q). */
export const sourceRowPicker = signal<{
  lineItemId: string;
  query: string;
  loading: boolean;
  result: SourceRowSearchResponse | null;
  error: string | null;
} | null>(null);
export const returnDialogOpen = signal<boolean>(false);
// Unified demand editor: the toolbar control switches the editable demand areas
// and the aggregate pricing/outcome summary (HS2-N2RP89).
export type EditorSegment = 'info' | 'lines' | 'quotes' | 'files' | 'summary' | 'decision-summary';
export const editorSegment = signal<EditorSegment>('lines');
// Which pane the Summary / Outcomes section shows, chosen by an in-content
// segmented control (HS2-D2W8M2): the pricing-check Overview, the business-office
// Decision Summary, or the purchase Outcomes.
export type SummaryPane = 'overview' | 'decision' | 'outcomes';
export const summaryPane = signal<SummaryPane>('overview');
// Supporting-docs markdown note: preview when false, raw-markdown edit when true
// (double-click to edit; blur to save). HS2-9K33CQ.
/** The note open in its inline editor (HS2-E33XQD); null when none is being edited. */
export const editingNoteId = signal<string | null>(null);

/** What a right-click context menu was opened for. Row targets carry what their entries need. */
export type ContextMenuTarget =
  | {
      kind: 'demand-row';
      demandId: string;
      cancelled: boolean;
      /** The row actions allowed when the menu opened, so a list refresh while it closes cannot reshape it. */
      actions: {
        cancel: boolean;
        reopen: boolean;
        duplicate: boolean;
        returnToRequester: boolean;
        /** Assignment commands (FR-5.20): the assignee seen when the menu opened, the viewer, and the staff. */
        assign: {
          current: string | null;
          viewer: string | null;
          members: readonly { id: string; name: string }[];
        } | null;
      };
    }
  | { kind: 'chat-row'; chatId: string; closed: boolean }
  | { kind: 'selection' }
  | { kind: 'library-documents' }
  | { kind: 'matrix-cells' };
/** The context menu currently open, or null. The menu's DOM exists only while this is set (HS2-3KAZPW);
 *  `id` distinguishes successive menus so a late close event cannot clear a newer one. */
export const contextMenu = signal<{ id: number; target: ContextMenuTarget; x: number; y: number } | null>(null);
/** Whether the aggregate attachment-failure details dialog is open. */
export const attachmentErrorsOpen = signal<boolean>(false);
/** The demand title (in the main toolbar) is in click-to-edit mode. */
export const titleEditing = signal<boolean>(false);
/** The demand description is in double-click-to-edit mode (markdown preview by default). */
export const descEditing = signal<boolean>(false);
/** The demand "Needed by" date is in click-to-edit mode (formatted-date preview by default). HS2-8TG0P1. */
export const neededByEditing = signal<boolean>(false);
/** The "New chat — pick a project" dialog is open (HS2-1E0YTJ). */
export const newChatDialogOpen = signal<boolean>(false);
export const quoteMatrix = signal<QuoteMatrix | null>(null);
export const quoteMatrixLoadState = signal<QuoteMatrixLoadState>({ status: 'idle' });
// Quotes redesign (HS2-0SV2XB): the add/edit-a-quotation dialog. null = closed;
// { mode: 'add' } opens a blank manual quotation; { mode: 'edit', quotationId }
// edits an existing quotation's header (vendor / currency / basis).
/** The quotation dialog: add, edit, or view-only on a read-only demand (HS2-MKHVY9). */
export const quoteDialog = signal<
  { mode: 'add' } | { mode: 'edit'; quotationId: string } | { mode: 'view'; quotationId: string } | null
>(null);
/** Keep the manual form's three-way tax choice through matrix polling and viewport rerenders. */
export const quoteTaxBasis = signal<'unknown' | 'included' | 'excluded'>('unknown');

/** The open-questions answer dialog (HS2-0H44BG): the workspace shows a compact
 *  banner; the open forms are answered in this dialog, opened from the banner's Answer button. */
export const questionsDialogOpen = signal<boolean>(false);
/** Server-backed draft loaded for the open questions form and current member. */
export const questionsDraft = signal<{ formId: string; answers: QuestionsDraft } | null>(null);

// Line-items row actions (HS2-1F4215): multi-select for batch actions, plus the
// decision dialog opened from the row kebab / right-click menu or the batch bar
// for actions that need a price (approve / negotiate).
export const selectedLineIds = signal<string[]>([]);
/** Line rows whose action menu has been opened in the current demand (HS2-D2G57R). Other rows render the
 *  menu's trigger with no entries, so a 300-line demand does not build 2,700 menu items on every render;
 *  the first open fills that row in and it keeps its menu, so focus can return to a live trigger. */
export const materializedRowMenus = signal<{ demandId: string; lineIds: ReadonlySet<string> }>({
  demandId: '',
  lineIds: new Set(),
});
/** How many line rows the current mount of the Line items list renders (HS2-HVR0HY). `key` names the
 *  mount (view, demand and segment); a new mount starts again from the first screenful. */
export const lineRowWindow = signal<{ key: string; count: number }>({ key: '', count: 0 });
export const lineSelectionAnchor = signal<string>('');
/** Supporting-document tile selection and image-preview state (HS2-N7XXA9). */
export const selectedDocumentIds = signal<string[]>([]);
export const documentSelectionAnchor = signal<string>('');
/** Price-matrix cell selection (HS2-QV3276): the selected cells' quote-line ids plus
 *  the Shift-range anchor; see lib/matrix-selection.ts. */
export const matrixSelection = signal<MatrixSelection>(EMPTY_MATRIX_SELECTION);
/** Where the price matrix's single keyboard tab stop rests (a cell or a header). */
export const matrixFocus = signal<MatrixPosition | null>(null);
export const documentPreview = signal<{
  documentId: string;
  url: string;
  source?: 'library';
  zoom?: number;
  annotating?: boolean;
  annotationsLoading?: boolean;
  annotationLoadFailed?: boolean;
  annotationSaving?: boolean;
  annotationMessage?: string;
  point?: { x: number; y: number };
} | null>(null);
export const documentAnnotations = signal<DocumentAnnotation[]>([]);
export const lineEditorDialog = signal<{ lineId: string | null; advancedOpen?: boolean } | null>(null);
export const lineEvidenceDialog = signal<{ lineId: string } | null>(null);
export const lineEvidenceView = signal<'pricing' | 'quotes' | 'benchmarks'>('pricing');
/** On compact devices the evidence split is a NavStack: true while the chosen
 *  view is pushed over the list (HS2-AW53XQ). Ignored on roomy devices. */
export const lineEvidenceDetailActive = signal<boolean>(false);
export const lineBenchmarkDialogOpen = signal<boolean>(false);
export const lineEvidenceMessage = signal<string>('');
export const lineEvidenceSubmitting = signal<boolean>(false);
export const lineDecisionDialog = signal<{ lineIds: string[]; action: string } | null>(null);
/** The line a reviewer is asking the requester to clarify (HS2-R5H22P); null when closed. */
export const clarifyDialog = signal<{ lineId: string } | null>(null);
/** The Mark Ordered / Edit Order dialog's line (FR-5.18, HS2-HYDRJX). */
export const orderDialog = signal<{ lineId: string; failing?: boolean } | null>(null);
/** The Record Receipt dialog's line (FR-5.19, HS2-6AJZPE). */
export const receiptDialog = signal<{ lineId: string } | null>(null);
/** Whether the Reopen with Update dialog is open (HS2-Z6MFSA). */
export const reopenUpdateOpen = signal<boolean>(false);
/** In the decision dialog (HS2-EKEE9S): the chosen quote line (null = the default
 *  best) and the selection reason label. Held in signals so the controlled
 *  selects survive re-renders (morph would otherwise reset them to their JSX
 *  values). Choosing a non-best quote reveals the reason control; a reason of
 *  "Other" reveals the free-text field. Reset when the dialog opens. */
export const decisionVendor = signal<string | null>(null);
export const decisionReason = signal<string>('');
/** Decision actions that carry a price and therefore open the dialog. */
export const DECISION_NEEDS_PRICE = new Set(['approve', 'negotiate']);

export const analysisList = signal<AnalysisRowT[]>([]);
export const analysisReport = signal<AnalysisReport | null>(null);
export const selectedAnalysisId = signal<string>('');
/** Snapshot-scoped draft: closing the dialog leaves the report untouched. */
export const assumptionsDialog = signal<{
  orgId: string;
  demandId: string;
  analysisId: string;
  base: string;
  values: Record<string, string>;
} | null>(null);
export const assumptionsSubmitting = signal(false);
export const assumptionsMessage = signal('');
export const analysisRunning = signal<boolean>(false);
// Live per-line progress of a running pricing check, streamed over the demand SSE
// channel (HS2-MHCQ1Y): `done`/`total` drive the "N of M lines benchmarked"
// counter and `lines` accumulates each line's verdict as it arrives. null when no
// run is in flight.
export interface AnalysisLiveLine {
  lineItemId: string;
  verdict: string;
}
export const analysisProgress = signal<{ done: number; total: number; lines: AnalysisLiveLine[] } | null>(null);

export const decisions = signal<DecisionT[]>([]);
/** The demand whose decisions `decisions` currently holds ('' until loaded), so
 * UI that depends on a line being undecided waits for real data (HS2-A0851K). */
export const decisionsDemandId = signal<string>('');
export const decisionSummary = signal<DecisionSummaryT | null>(null);
export const demandSavings = signal<DemandSavingsT | null>(null);
export const orgSavings = signal<OrgSavingsT | null>(null);
export const savingsBreakdownView = signal<'demand' | 'vendor' | 'month'>('demand');
export const savingsPeriod = signal<'all' | '90d' | '30d'>('all');
/** Selected site for the Savings rollup ('' = all sites). */
export const savingsSite = signal<string>('');
export const vendorsOverview = signal<VendorsOverviewT | null>(null);
export const vendorDetail = signal<VendorDetailT | null>(null);
/** The open Vendors dialog: `vendorId` null adds a vendor; `error` is the last failed save (HS2-BBZXKM). */
export const vendorDialog = signal<{ vendorId: string | null; error: string } | null>(null);
export const vendorDetailSection = signal<'details' | 'quotations' | 'purchases'>('details');
/** Organization data export: `running` from the click until the download starts (or fails). */
export const exportState = signal<{ status: 'idle' | 'running' }>({ status: 'idle' });
export const deleteConfirmOpen = signal<boolean>(false);
/** A navigation held because the explicit-save page has unsaved edits (HS2-HV4R7C). */
export const unsavedChangesPrompt = signal<{ page: ExplicitSavePage; saving: boolean } | null>(null);
/** A close held because a guarded dialog has unsaved edits; `subject` names what it edits (HS2-FSBQVX). */
export const dialogUnsavedPrompt = signal<{ subject: string } | null>(null);
/** The open Sites dialog: `siteId` null adds a site; `error` is the last failed save (HS2-DE4RP1). */
export const siteDialog = signal<{ siteId: string | null; error: string } | null>(null);
/**
 * The open Members dialog: the invitation form (HS2-FJ9J2K) or one member's details
 * (HS2-20JG34); `error` is the last failed action.
 */
export const memberDialog = signal<
  { kind: 'invite'; error: string } | { kind: 'edit'; memberId: string; error: string } | null
>(null);
/** The currencies shown in the landed-cost card's exchange-rate form (a draft: added and removed before saving). */
export const assumptionFxCodes = signal<string[]>([]);
/** Currency settings dialogs; the picker filter is cleared whenever it opens. */
export const currencyPickerOpen = signal(false);
export const currencyPickerFilter = signal('');
export const currencyEditCode = signal<string | null>(null);
/** The AI usage card's budget editor dialog. */
export const aiBudgetDialogOpen = signal<boolean>(false);
/** Which spend breakdown the AI usage card shows. */
export const aiSpendView = signal<'day' | 'analysis'>('day');

export { PRICE_BASES, SELECTION_REASONS } from '../../shared/enums.js';

export const libraryProjects = signal<ProjectT[]>([]);
export const libraryScope = signal<string>(''); // '' = organization, else projectId
export const libraryTab = signal<'documents' | 'facts'>('documents');
export const libraryFactDialogId = signal<string | null | undefined>(undefined); // null = new fact
export const libraryDocs = signal<BackgroundDocT[]>([]);
/** Selected Knowledge Base document tiles and the Shift-range anchor, like demand documents (HS2-KRH1MW). */
export const selectedLibraryDocIds = signal<string[]>([]);
export const librarySelectionAnchor = signal('');
export const libraryRevisions = signal<{ documentId: string; revisions: DocumentRevisionT[] } | null>(null);
export const libraryFacts = signal<FactT[]>([]);

// AI chat, demand- and library/project-scoped (HS2-ZXJJSS / HS2-Q19SYC).
export const chatTarget = signal<ChatTarget | null>(null);
export const chatConversations = signal<ConversationSummaryT[]>([]);
export const chatActiveId = signal<string>('');
export const chatMessages = signal<ChatMessageT[]>([]);
export const chatReadOnly = signal<boolean>(false);
export const chatSending = signal<boolean>(false);
export const chatHistoryOpen = signal<boolean>(false);
export const chatLiveActivity = signal<string>(''); // streamed tool-activity while a turn runs
// Incremental assistant text streamed over chat.delta while a turn runs, shown as
// a live bubble until chat.done reloads the persisted message (HS2-J7096Q).
export const chatLiveText = signal<string>('');
export const chatProjectId = signal<string>('');
export const chatSortField = signal<'created' | 'updated'>('updated');
export const chatSortDirection = signal<'asc' | 'desc'>('desc');
export const chatTitleEditing = signal<boolean>(false);

export { DECISION_ACTIONS } from '../../shared/enums.js';
